import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  AdminToolsData,
  AdminSQLQuery,
  DatabaseTable,
  QueryTemplate,
  BulkOperation,
  AdminAuditLogEntry,
  AuditLogStats,
  FeatureFlag,
  SystemSetting,
  MaintenanceOperation,
  EnvironmentInfo,
  SQLQueryResult,
} from '../../types/admin';
import { subDays, subHours, format } from 'date-fns';

/**
 * useAdminTools - Hook for admin tools and utilities
 *
 * Generates realistic mock data for:
 * - Database tools (query history, schema, templates)
 * - Bulk operations
 * - Audit log
 * - System configuration (feature flags, settings)
 */

// Helper functions
function randomWithVariance(base: number, variance: number): number {
  return Math.round(base + (Math.random() - 0.5) * 2 * variance);
}

function randomIP(): string {
  return `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;
}

// Generate query history
function generateQueryHistory(): AdminSQLQuery[] {
  const queries: AdminSQLQuery[] = [];
  const sampleQueries = [
    'SELECT * FROM users WHERE subscription_tier = "pro" ORDER BY created_at DESC LIMIT 100',
    'SELECT COUNT(*) as count FROM homes WHERE created_at >= datetime("now", "-7 days")',
    'SELECT b.name, COUNT(a.id) as agent_count FROM businesses b LEFT JOIN agents a ON b.id = a.business_id GROUP BY b.id',
    'SELECT tier, COUNT(*) as count FROM users GROUP BY subscription_tier',
    'SELECT * FROM users WHERE email LIKE "%gmail.com" LIMIT 50',
  ];

  for (let i = 0; i < 50; i++) {
    const query = sampleQueries[Math.floor(Math.random() * sampleQueries.length)];
    const executedAt = subHours(new Date(), i * 2 + randomWithVariance(0, 1));
    const isError = Math.random() < 0.05;

    queries.push({
      id: `query_${i + 1}`,
      query,
      executed_at: format(executedAt, 'yyyy-MM-dd HH:mm:ss'),
      execution_time_ms: randomWithVariance(45, 30),
      rows_returned: isError ? 0 : randomWithVariance(150, 100),
      error: isError ? 'Syntax error near "LIMIT"' : undefined,
    });
  }

  return queries.sort((a, b) => b.executed_at.localeCompare(a.executed_at));
}

// Generate database schema
function generateDatabaseSchema(): DatabaseTable[] {
  return [
    {
      table_name: 'users',
      row_count: 15420,
      size_mb: 24.5,
      indexes: ['idx_users_email', 'idx_users_subscription_tier', 'idx_users_created_at'],
      foreign_keys: [],
    },
    {
      table_name: 'homes',
      row_count: 8932,
      size_mb: 18.2,
      indexes: ['idx_homes_user_id', 'idx_homes_created_at', 'idx_homes_status'],
      foreign_keys: [{ column: 'user_id', references_table: 'users', references_column: 'id' }],
    },
    {
      table_name: 'businesses',
      row_count: 432,
      size_mb: 2.1,
      indexes: ['idx_businesses_owner_id', 'idx_businesses_created_at'],
      foreign_keys: [{ column: 'owner_id', references_table: 'users', references_column: 'id' }],
    },
    {
      table_name: 'agents',
      row_count: 1240,
      size_mb: 3.8,
      indexes: ['idx_agents_business_id', 'idx_agents_user_id'],
      foreign_keys: [
        { column: 'business_id', references_table: 'businesses', references_column: 'id' },
        { column: 'user_id', references_table: 'users', references_column: 'id' },
      ],
    },
    {
      table_name: 'agent_clients',
      row_count: 3240,
      size_mb: 5.2,
      indexes: ['idx_agent_clients_agent_id', 'idx_agent_clients_client_id'],
      foreign_keys: [
        { column: 'agent_id', references_table: 'agents', references_column: 'id' },
        { column: 'client_id', references_table: 'users', references_column: 'id' },
      ],
    },
    {
      table_name: 'home_maintenance',
      row_count: 12450,
      size_mb: 8.9,
      indexes: ['idx_maintenance_home_id', 'idx_maintenance_due_date'],
      foreign_keys: [{ column: 'home_id', references_table: 'homes', references_column: 'id' }],
    },
    {
      table_name: 'chat_messages',
      row_count: 45230,
      size_mb: 32.4,
      indexes: ['idx_messages_room_id', 'idx_messages_created_at'],
      foreign_keys: [],
    },
    {
      table_name: 'activity_logs',
      row_count: 89432,
      size_mb: 45.2,
      indexes: ['idx_activity_user_id', 'idx_activity_created_at'],
      foreign_keys: [{ column: 'user_id', references_table: 'users', references_column: 'id' }],
    },
  ];
}

// Generate query templates
function generateQueryTemplates(): QueryTemplate[] {
  return [
    {
      id: 'template_1',
      name: 'Recent User Signups',
      description: 'Users who signed up in the last 7 days',
      category: 'Users',
      sql: 'SELECT id, email, name, created_at FROM users WHERE created_at >= datetime("now", "-7 days") ORDER BY created_at DESC',
    },
    {
      id: 'template_2',
      name: 'Active Subscriptions by Tier',
      description: 'Count of active subscriptions grouped by tier',
      category: 'Billing',
      sql: 'SELECT subscription_tier, COUNT(*) as count FROM users WHERE subscription_tier != "free" GROUP BY subscription_tier',
    },
    {
      id: 'template_3',
      name: 'Properties Created This Month',
      description: 'All properties created in current month',
      category: 'Properties',
      sql: 'SELECT h.id, h.name, u.email as owner_email, h.created_at FROM homes h JOIN users u ON h.user_id = u.id WHERE strftime("%Y-%m", h.created_at) = strftime("%Y-%m", "now") ORDER BY h.created_at DESC',
    },
    {
      id: 'template_4',
      name: 'Agent Performance',
      description: 'Agents with client counts and business info',
      category: 'Business',
      sql: 'SELECT a.name, a.email, COUNT(ac.id) as client_count, b.name as business_name FROM agents a LEFT JOIN agent_clients ac ON a.id = ac.agent_id JOIN businesses b ON a.business_id = b.id GROUP BY a.id ORDER BY client_count DESC',
    },
    {
      id: 'template_5',
      name: 'Failed Payments',
      description: 'Recent failed payment attempts',
      category: 'Billing',
      sql: 'SELECT user_id, amount, error_message, created_at FROM payment_failures WHERE created_at >= datetime("now", "-30 days") ORDER BY created_at DESC',
    },
    {
      id: 'template_6',
      name: 'Most Active Chat Rooms',
      description: 'Chat rooms with most messages in last 7 days',
      category: 'Chat',
      sql: 'SELECT room_id, COUNT(*) as message_count FROM chat_messages WHERE created_at >= datetime("now", "-7 days") GROUP BY room_id ORDER BY message_count DESC LIMIT 20',
    },
    {
      id: 'template_7',
      name: 'Flagged Content',
      description: 'Properties and users flagged for review',
      category: 'Moderation',
      sql: 'SELECT entity_type, entity_id, flag_reason, flagged_at FROM content_flags WHERE status = "pending" ORDER BY flagged_at DESC',
    },
    {
      id: 'template_8',
      name: 'Maintenance Tasks Due',
      description: 'Upcoming maintenance tasks',
      category: 'Maintenance',
      sql: 'SELECT m.id, m.title, h.name as home_name, m.due_date FROM home_maintenance m JOIN homes h ON m.home_id = h.id WHERE m.due_date BETWEEN datetime("now") AND datetime("now", "+30 days") ORDER BY m.due_date',
    },
  ];
}

// Generate bulk operations
function generateBulkOperations(): BulkOperation[] {
  const operations: BulkOperation[] = [];
  const opTypes = [
    { type: 'Export Users to CSV', entity: 'user' as const },
    { type: 'Bulk Archive Properties', entity: 'property' as const },
    { type: 'Bulk Email Notification', entity: 'user' as const },
    { type: 'Export Businesses to CSV', entity: 'business' as const },
    { type: 'Bulk Unflag Properties', entity: 'property' as const },
  ];

  for (let i = 0; i < 15; i++) {
    const opType = opTypes[Math.floor(Math.random() * opTypes.length)];
    const totalItems = randomWithVariance(500, 400);
    const statusRand = Math.random();
    let status: BulkOperation['status'] = 'completed';
    let processedItems = totalItems;
    let successfulItems = totalItems;
    let failedItems = 0;

    if (statusRand < 0.1) {
      status = 'processing';
      processedItems = Math.floor(totalItems * 0.6);
      successfulItems = processedItems - Math.floor(processedItems * 0.05);
      failedItems = processedItems - successfulItems;
    } else if (statusRand < 0.15) {
      status = 'failed';
      processedItems = Math.floor(totalItems * 0.3);
      successfulItems = 0;
      failedItems = processedItems;
    } else {
      failedItems = Math.floor(totalItems * 0.02);
      successfulItems = totalItems - failedItems;
    }

    const startedAt = subDays(new Date(), Math.floor(i / 3));

    operations.push({
      id: `bulk_${i + 1}`,
      operation_type: opType.type,
      entity_type: opType.entity,
      total_items: totalItems,
      processed_items: processedItems,
      successful_items: successfulItems,
      failed_items: failedItems,
      status,
      started_at: format(startedAt, 'yyyy-MM-dd HH:mm:ss'),
      completed_at: status === 'completed' ? format(subHours(startedAt, -1), 'yyyy-MM-dd HH:mm:ss') : undefined,
      started_by: 'admin@cribcritic.com',
      errors:
        failedItems > 0
          ? [
              { item_id: 'item-123', error_message: 'User account is banned' },
              { item_id: 'item-456', error_message: 'Property has active maintenance tasks' },
            ]
          : undefined,
    });
  }

  return operations.sort((a, b) => b.started_at.localeCompare(a.started_at));
}

// Generate audit log
function generateAuditLog(): AdminAuditLogEntry[] {
  const entries: AdminAuditLogEntry[] = [];
  const admins = [
    { id: 'admin-1', email: 'admin@cribcritic.com', name: 'John Admin' },
    { id: 'admin-2', email: 'support@cribcritic.com', name: 'Sarah Support' },
    { id: 'admin-3', email: 'dev@cribcritic.com', name: 'Mike Developer' },
  ];

  const actions = [
    {
      type: 'delete' as const,
      desc: 'Deleted user account',
      entity: 'user' as const,
      entity_name: 'user@example.com',
      details: { reason: 'GDPR deletion request' },
    },
    {
      type: 'bulk_operation' as const,
      desc: 'Exported 1500 users to CSV',
      entity: 'user' as const,
      entity_name: null,
      details: { filters: { tier: 'pro' }, row_count: 1500 },
    },
    {
      type: 'system_config' as const,
      desc: 'Enabled feature flag: new_dashboard',
      entity: 'system' as const,
      entity_name: null,
      details: { feature_flag: 'new_dashboard', previous_value: false, new_value: true },
    },
    {
      type: 'update' as const,
      desc: 'Updated business subscription',
      entity: 'business' as const,
      entity_name: 'Acme Realty',
      details: { from_tier: 'pro', to_tier: 'agent' },
    },
    {
      type: 'view' as const,
      desc: 'Viewed user details',
      entity: 'user' as const,
      entity_name: 'user@example.com',
      details: { sections: ['profile', 'billing'] },
    },
    {
      type: 'export' as const,
      desc: 'Exported property data',
      entity: 'property' as const,
      entity_name: null,
      details: { format: 'csv', count: 250 },
    },
  ];

  for (let i = 0; i < 150; i++) {
    const admin = admins[Math.floor(Math.random() * admins.length)];
    const action = actions[Math.floor(Math.random() * actions.length)];
    const createdAt = subHours(new Date(), i + randomWithVariance(0, 2));
    const isSuccess = Math.random() > 0.05;

    entries.push({
      id: `audit_${i + 1}`,
      admin_user_id: admin.id,
      admin_email: admin.email,
      admin_name: admin.name,
      action_type: action.type,
      action_description: action.desc,
      entity_type: action.entity,
      entity_id: action.entity_name ? `entity-${Math.floor(Math.random() * 10000)}` : undefined,
      entity_name: action.entity_name || undefined,
      action_details: action.details,
      ip_address: randomIP(),
      user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      execution_time_ms: randomWithVariance(150, 100),
      success: isSuccess,
      error_message: !isSuccess ? 'Operation timed out' : undefined,
      created_at: format(createdAt, 'yyyy-MM-dd HH:mm:ss'),
    });
  }

  return entries.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

// Calculate audit log stats
function calculateAuditLogStats(auditLog: AdminAuditLogEntry[]): AuditLogStats {
  const actionsByAdmin: Map<string, { admin_email: string; admin_name: string; count: number }> = new Map();
  const actionsByType: Map<string, number> = new Map();
  const actionsByEntity: Map<string, number> = new Map();
  let failedActions = 0;

  auditLog.forEach((entry) => {
    // By admin
    const adminKey = entry.admin_email;
    if (!actionsByAdmin.has(adminKey)) {
      actionsByAdmin.set(adminKey, { admin_email: entry.admin_email, admin_name: entry.admin_name, count: 0 });
    }
    actionsByAdmin.get(adminKey)!.count++;

    // By type
    actionsByType.set(entry.action_type, (actionsByType.get(entry.action_type) || 0) + 1);

    // By entity
    actionsByEntity.set(entry.entity_type, (actionsByEntity.get(entry.entity_type) || 0) + 1);

    // Failed actions
    if (!entry.success) failedActions++;
  });

  const totalActions = auditLog.length;

  return {
    total_actions: totalActions,
    actions_by_admin: Array.from(actionsByAdmin.values()).sort((a, b) => b.count - a.count),
    actions_by_type: Array.from(actionsByType.entries())
      .map(([type, count]) => ({
        type,
        count,
        percentage: (count / totalActions) * 100,
      }))
      .sort((a, b) => b.count - a.count),
    actions_by_entity: Array.from(actionsByEntity.entries())
      .map(([entity_type, count]) => ({
        entity_type,
        count,
      }))
      .sort((a, b) => b.count - a.count),
    failed_actions: failedActions,
    date_range: {
      start: auditLog[auditLog.length - 1]?.created_at || '',
      end: auditLog[0]?.created_at || '',
    },
  };
}

// Generate feature flags
function generateFeatureFlags(): FeatureFlag[] {
  return [
    {
      id: 'flag_1',
      name: 'New Dashboard',
      key: 'new_dashboard',
      description: 'Enable the redesigned admin dashboard with enhanced analytics',
      enabled: true,
      rollout_percentage: 100,
      enabled_for_users: [],
      enabled_for_businesses: [],
      created_at: format(subDays(new Date(), 40), 'yyyy-MM-dd HH:mm:ss'),
      updated_at: format(subDays(new Date(), 1), 'yyyy-MM-dd HH:mm:ss'),
      last_modified_by: 'admin@cribcritic.com',
    },
    {
      id: 'flag_2',
      name: 'BigQuery Integration',
      key: 'bigquery_integration',
      description: 'Enable BigQuery data export for advanced analytics',
      enabled: false,
      rollout_percentage: 0,
      enabled_for_users: ['user-123', 'user-456'],
      enabled_for_businesses: ['biz-789'],
      created_at: format(subDays(new Date(), 10), 'yyyy-MM-dd HH:mm:ss'),
      updated_at: format(subDays(new Date(), 2), 'yyyy-MM-dd HH:mm:ss'),
      last_modified_by: 'dev@cribcritic.com',
    },
    {
      id: 'flag_3',
      name: 'Enhanced Chat',
      key: 'enhanced_chat',
      description: 'New chat interface with file sharing and reactions',
      enabled: true,
      rollout_percentage: 50,
      enabled_for_users: [],
      enabled_for_businesses: [],
      created_at: format(subDays(new Date(), 5), 'yyyy-MM-dd HH:mm:ss'),
      updated_at: format(subHours(new Date(), 12), 'yyyy-MM-dd HH:mm:ss'),
      last_modified_by: 'admin@cribcritic.com',
    },
    {
      id: 'flag_4',
      name: 'Mobile App Beta',
      key: 'mobile_app_beta',
      description: 'Access to mobile app beta features',
      enabled: false,
      rollout_percentage: 10,
      enabled_for_users: ['user-100', 'user-101', 'user-102'],
      enabled_for_businesses: [],
      created_at: format(subDays(new Date(), 3), 'yyyy-MM-dd HH:mm:ss'),
      updated_at: format(subDays(new Date(), 3), 'yyyy-MM-dd HH:mm:ss'),
      last_modified_by: 'dev@cribcritic.com',
    },
    {
      id: 'flag_5',
      name: 'AI Property Insights',
      key: 'ai_property_insights',
      description: 'AI-powered property analysis and recommendations',
      enabled: true,
      rollout_percentage: 75,
      enabled_for_users: [],
      enabled_for_businesses: ['biz-1', 'biz-2'],
      created_at: format(subDays(new Date(), 15), 'yyyy-MM-dd HH:mm:ss'),
      updated_at: format(subDays(new Date(), 1), 'yyyy-MM-dd HH:mm:ss'),
      last_modified_by: 'admin@cribcritic.com',
    },
  ];
}

// Generate system settings
function generateSystemSettings(): SystemSetting[] {
  return [
    {
      key: 'maintenance_mode',
      value: false,
      description: 'Enable maintenance mode to prevent user access',
      type: 'boolean',
      category: 'general',
      last_updated: format(subDays(new Date(), 30), 'yyyy-MM-dd HH:mm:ss'),
      last_updated_by: 'system',
    },
    {
      key: 'max_properties_free',
      value: 1,
      description: 'Maximum number of properties for free tier users',
      type: 'number',
      category: 'limits',
      validation: 'min:1,max:10',
      last_updated: format(subDays(new Date(), 15), 'yyyy-MM-dd HH:mm:ss'),
      last_updated_by: 'admin@cribcritic.com',
    },
    {
      key: 'max_properties_pro',
      value: 5,
      description: 'Maximum number of properties for pro tier users',
      type: 'number',
      category: 'limits',
      validation: 'min:1,max:100',
      last_updated: format(subDays(new Date(), 15), 'yyyy-MM-dd HH:mm:ss'),
      last_updated_by: 'admin@cribcritic.com',
    },
    {
      key: 'max_properties_agent',
      value: 50,
      description: 'Maximum number of properties for agent tier users',
      type: 'number',
      category: 'limits',
      validation: 'min:1,max:1000',
      last_updated: format(subDays(new Date(), 15), 'yyyy-MM-dd HH:mm:ss'),
      last_updated_by: 'admin@cribcritic.com',
    },
    {
      key: 'trial_period_days',
      value: 14,
      description: 'Trial period duration in days for pro subscriptions',
      type: 'number',
      category: 'billing',
      validation: 'min:0,max:90',
      last_updated: format(subDays(new Date(), 60), 'yyyy-MM-dd HH:mm:ss'),
      last_updated_by: 'admin@cribcritic.com',
    },
    {
      key: 'email_notifications_enabled',
      value: true,
      description: 'Enable email notifications system-wide',
      type: 'boolean',
      category: 'notifications',
      last_updated: format(subDays(new Date(), 5), 'yyyy-MM-dd HH:mm:ss'),
      last_updated_by: 'admin@cribcritic.com',
    },
    {
      key: 'api_rate_limit_per_minute',
      value: 60,
      description: 'API rate limit per user per minute',
      type: 'number',
      category: 'performance',
      validation: 'min:10,max:1000',
      last_updated: format(subDays(new Date(), 20), 'yyyy-MM-dd HH:mm:ss'),
      last_updated_by: 'dev@cribcritic.com',
    },
    {
      key: 'cache_ttl_seconds',
      value: 300,
      description: 'Default cache TTL in seconds',
      type: 'number',
      category: 'performance',
      validation: 'min:60,max:3600',
      last_updated: format(subDays(new Date(), 10), 'yyyy-MM-dd HH:mm:ss'),
      last_updated_by: 'dev@cribcritic.com',
    },
  ];
}

// Generate maintenance operations
function generateMaintenanceOperations(): MaintenanceOperation[] {
  return [
    {
      id: 'maint_1',
      operation: 'clear_all_caches',
      description: 'Clear all KV and R2 caches',
      status: 'idle',
    },
    {
      id: 'maint_2',
      operation: 'rebuild_search_indexes',
      description: 'Rebuild search indexes for properties and users',
      status: 'idle',
    },
    {
      id: 'maint_3',
      operation: 'recalculate_metrics',
      description: 'Recalculate subscription and revenue metrics',
      status: 'idle',
    },
    {
      id: 'maint_4',
      operation: 'sync_paddle_webhooks',
      description: 'Sync pending Paddle webhook events',
      status: 'idle',
    },
    {
      id: 'maint_5',
      operation: 'optimize_database',
      description: 'Run VACUUM and ANALYZE on database',
      status: 'idle',
    },
  ];
}

// Generate environment info
function generateEnvironmentInfo(): EnvironmentInfo {
  return {
    environment: 'production',
    worker_version: '2.4.1',
    database_version: 'D1-2023.11.0',
    last_deployment: format(subDays(new Date(), 2), 'yyyy-MM-dd HH:mm:ss'),
    uptime: 172800, // 2 days in seconds
    feature_flags_count: 5,
    active_feature_flags: 3,
  };
}

export function useAdminTools() {
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const toolsData = useMemo<AdminToolsData>(() => {
    const auditLog = generateAuditLog();

    return {
      // Database Tools
      queryHistory: generateQueryHistory(),
      databaseSchema: generateDatabaseSchema(),
      queryTemplates: generateQueryTemplates(),

      // Bulk Operations
      bulkOperations: generateBulkOperations(),

      // Audit Log
      auditLog,
      auditLogStats: calculateAuditLogStats(auditLog),

      // System Configuration
      featureFlags: generateFeatureFlags(),
      systemSettings: generateSystemSettings(),
      maintenanceOperations: generateMaintenanceOperations(),
      environmentInfo: generateEnvironmentInfo(),

      lastUpdated: format(lastRefresh, 'yyyy-MM-dd HH:mm:ss'),
    };
  }, [lastRefresh]);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, [lastRefresh]);

  const refresh = () => setLastRefresh(new Date());

  const executeQuery = useCallback(async (query: string): Promise<SQLQueryResult> => {
    console.log('Executing query:', query);
    // Simulate query execution
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Mock result
    return {
      columns: ['id', 'email', 'name', 'created_at'],
      rows: [
        ['1', 'user1@example.com', 'User One', '2025-11-01 10:00:00'],
        ['2', 'user2@example.com', 'User Two', '2025-11-02 11:30:00'],
        ['3', 'user3@example.com', 'User Three', '2025-11-03 14:15:00'],
      ],
      execution_time_ms: 45,
      row_count: 3,
      query,
    };
  }, []);

  const toggleFeatureFlag = useCallback(async (flagId: string) => {
    console.log('Toggling feature flag:', flagId);
    setTimeout(() => refresh(), 500);
    return { success: true };
  }, []);

  const updateSystemSetting = useCallback(async (key: string, value: any) => {
    console.log('Updating system setting:', key, value);
    setTimeout(() => refresh(), 500);
    return { success: true };
  }, []);

  const runMaintenanceOperation = useCallback(async (operationId: string) => {
    console.log('Running maintenance operation:', operationId);
    setTimeout(() => refresh(), 2000);
    return { success: true };
  }, []);

  return {
    toolsData,
    isLoading,
    refresh,
    executeQuery,
    toggleFeatureFlag,
    updateSystemSetting,
    runMaintenanceOperation,
  };
}
